<template>
    <div>
        <div v-if="paginate || filterable" class="inv-table-header">
            <div v-if="filterable" class="inv-table-filter">
                <label @click="focusSearch">
                    <span>{{ tableLang.filter }}</span>
                </label>
                <input type="search"
                       ref="search"
                       class="form-control"
                       :placeholder="tableLang.filter_placeholder"
                       v-model="filter"
                       @input="filterRecords">
            </div>
            <div v-if="paginate" class="inv-table-length">
                <label>
                    <span>{{ tableLang.perPage }}</span>
                </label>
                <select2 v-model="perPage" :options="pageLengths"></select2>
            </div>
        </div>

        <table :class="tableClass" ref="table">
            <thead v-if="thead">
            <tr>
                <th v-for="column in tableColumns" :class="colClass(column)" @click.prevent="sortRecords(column)">
                    {{ column.display }}
                    <i v-if="column.sort" :class="sortClass(column.key)"></i>
                </th>
            </tr>
            <slot name="thead"></slot>
            </thead>
            <tbody>
            <template v-if="tableRecords.length > 0">
                <tr v-for="record in paginatedRecords" :class="rowClass(record)" @click="rowClick(record)">
                    <template v-for="column in columns">
                        <td v-if="! column.is" v-html="renderColumn(record, column)" :class="colClass(column, false)"></td>
                        <td v-else :class="colClass(column, false)">
                            <component :is="column.is" :element-data="column.isProps"
                                       v-if="authorizeColumn(record, column)"
                                       :model="{record, column}"
                                       @custom-event="onCustom"
                            ></component>
                        </td>
                    </template>
                </tr>
            </template>
            <template v-else>
                <tr class="text-center">
                    <td :colspan="colspan">
                        {{ emptyText }}
                    </td>
                </tr>
            </template>
            </tbody>
            <tfoot v-if="tfoot">
            <tr>
                <th :class="colClass(column)" v-for="column in tableColumns">
                    {{ column.display }}
                    <i v-if="column.sort" :class="sortClass(column.key)"></i>
                </th>
            </tr>
            <slot name="tfoot"></slot>
            </tfoot>
        </table>

        <paginator
                v-if="paginate"
                :showShowing="showShowing"
                :page_data="paginationData"
                :lang="tableLang.paginator"
                @paginate="paginateRecords"
        >
        </paginator>

    </div>
</template>

<script>
    import _ from 'lodash';
    import Paginator from '../pagination/paginator.vue';

    export default {
        name: 'datatable',

        components: {
            Paginator
        },

        props: {
            addTableClass: {
                default: () => []
            },
            removeTableClass: {
                default: () => []
            },
            columns: {
                required: true
            },
            thead: {
                type: Boolean,
                default: true
            },
            tfoot: {
                type: Boolean,
                default: false
            },
            sortOrder: {
                type: Object,
                default: () => {
                    return { by: '', dir: 'asc' };
                }
            },
            records: {
                required: true
            },
            activeRowClass: {
                default: 'tr_active'
            },
            per_page: {
                default: 25
            },
            paginate: {
                type: Boolean,
                default: true
            },
            filterable: {
                type: Boolean,
                default: true
            },
            showShowing: {
                type: Boolean,
                default: true
            },
            pageLengthOptions: {
                default: () => ({
                    10: 10,
                    25: 25,
                    50: 50,
                    99999: 'All'
                })
            },
            clickable: {
                type: Boolean,
                default: true
            },
            lang: {
                default: () => ({})
            },
            addActiveClass: {
                type: Boolean,
                default: true,
            }
        },

        data() {
            return {
                tableColumns: this.getColumns(),
                sort: this.sortOrder,
                activeRecord: null,
                tableRecords: this.records,
                perPage: this.per_page,
                tableLang: this.setLang(),
                filter: '',
                originalData: null,
                currentPage: 1
            };
        },

        computed: {
            canSort() {
                let sort = false;

                _.forEach(this.tableColumns, (column) => {
                    if (column.sort) {
                        sort = true;
                        return false;
                    }
                });

                return sort;
            },
            colspan() {
                return this.columns.length;
            },
            emptyText() {
                if (_.trim(this.filter) === '') {
                    return this.tableLang.emptyTable;
                }

                return this.tableLang.noResults;
            },
            numPages() {
                return _.ceil(this.tableRecords.length / this.perPage);
            },
            paginationData() {
                return {
                    current_page: this.currentPage,
                    prev_page_url: null,
                    next_page_url: null,
                    from: (this.paginateBegin + 1),
                    to: this.paginateTo,
                    total: this.tableRecords.length,
                    per_page: this.perPage,
                    last_page: this.numPages,
                    perPage: this.perPage
                };
            },
            paginateBegin() {
                return ((this.currentPage - 1) * this.perPage);
            },
            paginateEnd() {
                return this.paginateBegin + this.perPage;
            },
            paginateTo() {
                if (this.paginateEnd > this.tableRecords.length) {
                    return this.tableRecords.length;
                }
                return this.paginateEnd;
            },
            paginatedRecords() {
                if (this.paginate) {
                    let paginatedRecords = this.tableRecords.slice(this.paginateBegin, this.paginateEnd);

                    return paginatedRecords;
                }

                return this.tableRecords;
            },
            pageLengths() {
                let lengths = [];

                for (let length in this.pageLengthOptions) {
                    lengths.push({
                        id: length,
                        text: this.pageLengthOptions[length]
                    });
                }

                return lengths;
            },
            tableClass() {
                let classList = [ 'table', 'table-striped', 'table-bordered', 'table-condensed', 'table-hover' ];

                if (! _.isNil(this.removeTableClass)) {
                    let to_remove = this.removeTableClass;
                    if (! _.isArray(to_remove)) {
                        to_remove = [ to_remove ];
                    }

                    classList = _.remove(classList, (c) => {
                        return $.inArray(c, to_remove) === -1;
                    });
                }

                if (! _.isNil(this.addTableClass)) {
                    let to_add = this.addTableClass;
                    if (! _.isArray(to_add)) {
                        to_add = [ to_add ];
                    }

                    classList = _.merge(classList, to_add);
                }

                return classList;
            },
        },

        mounted() {
            if (this.canSort) {
                let sortColumn = _.find(this.tableColumns, (column) => {
                    return column.key == this.sort.by;
                });

                if (! _.isNil(sortColumn)) {
                    this.sort.column = sortColumn;
                }

                this.sortRecords(null, false);
            }

            if (this.filterable) {
                this.originalData = this.records;
            }
        },

        methods: {
            focusSearch () {
                $(this.$refs.search).focus();
            },

            /**
             * Determine the class for a column.
             *
             * @param {object} column
             * @param {bool} thead
             */
            colClass(column, thead = true) {
                let classList = [];

                if (thead && column.sort) {
                    classList.push('cursor-pointer');
                }

                for (let index in column.classList) {
                    classList.push(column.classList[index]);
                }

                return classList;
            },

            /**
             * Filter the table's records.
             */
            filterRecords() {
                if (this.filterable) {
                    let query = _.trim(this.filter);

                    if (query == '') {
                        this.tableRecords = this.originalData;
                    } else {
                        let filtered = _.filter(this.originalData, (record) => {
                            let passed = false;

                            _.forEach(this.tableColumns, (column) => {
                                if (column.filter) {
                                    let columnValue = '';

                                    if (_.isFunction(column.filterVal)) {
                                        columnValue = column.filterVal(record);
                                    } else if (_.isFunction(column.render)) {
                                        columnValue = column.render(record);
                                    } else if (record[column.key]) {
                                        columnValue = record[column.key];
                                    }

                                    if (_.includes(_.toLower(columnValue), _.toLower(query))) {
                                        passed = true;

                                        // end foreach, record has passed the filter check
                                        return false;
                                    }
                                }
                            });

                            return passed;
                        });

                        this.tableRecords = filtered;
                    }

                    if (this.canSort) {
                        this.sortRecords(null, false);
                    }
                }
            },

            /**
             * Determine table columns.
             */
            getColumns() {
                let columns = [];

                _.forEach(this.columns, (column) => {
                    let local_column = {
                        display: _.defaultTo(column.display, ''),
                        key: _.defaultTo(column.key, ''),
                        sort: _.defaultTo(column.sort, true),
                        render: _.defaultTo(column.render, null),
                        classList: _.defaultTo(column.classList, []),
                        filter: _.defaultTo(column.filter, true),
                        filterVal: _.defaultTo(column.filterVal, null),
                        sortVal: _.defaultTo(column.sortVal, null)
                    };

                    columns.push(local_column);
                });

                return columns;
            },

            /**
             * Go to a record.
             *
             * @param {integer} id
             */
            goToRecord(id) {
                let index = _.findIndex(this.tableRecords, (record) => {
                    return record.id == id;
                });

                if (index > -1) {
                    let page = _.ceil((index + 1) / this.perPage);

                    if (page > 0) {
                        this.currentPage = page;
                        let record = this.tableRecords[index];

                        if (record) {
                            setTimeout(() => {
                                this.rowClick(record);
                            }, 250);
                        }
                    }
                }
            },

            /**
             * A column's custom event was fired.
             *
             * @param event
             * @param model
             */
            onCustom(event, model) {
                event.preventDefault();

                if (model && model.column && model.column.prevent) {
                    event.stopPropagation();
                }

                this.$emit('custom-event', event, model);
            },

            /**
             * Authorize a custom column.
             *
             * @param record
             * @param column
             */
            authorizeColumn (record, column) {
                if (column.isProps && _.isFunction(column.isProps.authorize)) {
                    return column.isProps.authorize(record);
                }

                return true;
            },

            /**
             * Go to a specific page of records.
             *
             * @param {integer} page
             */
            paginateRecords(page) {
                this.currentPage = page;
            },

            /**
             * Render a column in to the table.
             *
             * @param {object} record
             * @param {object} column
             */
            renderColumn(record, column) {
                let display = '';

                if (_.isFunction(column.render)) {
                    display = column.render(record);
                } else if (record[column.key]) {
                    display = record[column.key];
                }

                return display;
            },

            /**
             * Determine the class for a row.
             *
             * @param {object} record
             */
            rowClass(record) {
                let classList = [ 'cursor-pointer' ];

                if (record === this.activeRecord && this.addActiveClass) {
                    classList.push(this.activeRowClass);
                }

                return classList;
            },

            /**
             * Handle a row click
             *
             * @param {object} record
             */
            rowClick(record) {
                if (this.clickable) {
                    this.activeRecord = record;
                    this.$emit('row-click', record);
                }
            },

            /**
             * Set table language.
             */
            setLang() {
                let defaults = {
                    perPage: 'Show:',
                    filter: 'Filter:',
                    filter_placeholder: 'Type to filter...',
                    emptyTable: 'No records available.',
                    noResults: 'No matching records found.',
                    paginator: {
                        showingText: 'Showing',
                        ofText: 'of',
                        separator: '-',
                        endText: ['', '']
                    }
                };

                return _.merge(defaults, this.lang);
            },

            /**
             * Determine the class for a column's sort control
             *
             * @param {string} key
             */
            sortClass(key) {
                let classList = [ 'cursor-pointer' ];

                if (key === this.sort.by) {
                    let arrow_class = this.sort.dir === 'asc' ? 'up' : 'down';
                    classList.push('icon-arrow-' + arrow_class + '5');
                } else {
                    classList.push('icon-menu-open');
                    classList.push('text-muted');
                }

                return classList;
            },

            /**
             * Sort the records.
             *
             * @param {object} column
             * @param {bool} change_dir
             */
            sortRecords(column = null, change_dir = true) {
                if (this.canSort) {
                    if (_.isNil(column)) {
                        column = this.sort.column;

                        if (_.isNil(column)) {
                            return;
                        }
                    }

                    if (column.key === this.sort.by && change_dir) {
                        this.sort.dir = this.sort.dir === 'asc' ? 'desc' : 'asc';
                    } else {
                        if (column.key !== this.sort.by) {
                            this.sort.by = column.key;
                            this.sort.dir = 'asc';
                            this.sort.column = column;
                        }
                    }

                    let records = _.orderBy(this.tableRecords, [(record) => {
                        if (_.isFunction(column.sortVal)) {
                            return column.sortVal(record);
                        }

                        if (_.isFunction(column.render)) {
                            return column.render(record);
                        }

                        if (record[column.key]) {
                            try {
                                return record[column.key].toString().toLowerCase();
                            } catch (e) {
                                return record[column.key];
                            }
                        }

                        return '';
                    }], [this.sort.dir]);

                    this.tableRecords = records;

                    if (change_dir) {
                        this.currentPage = 1;
                    }

                }
            },

            /**
             * Update table records.
             *
             * @param {array} records
             * @param {object} record
             */
            updateRecords(records, record = null) {
                this.tableRecords = records;
                this.sortRecords(null, false);

                if (_.isNil(this.originalData) || this.originalData.length <= 0) {
                    this.originalData = records;
                }

                if (! _.isNil(record)) {
                    this.activeRecord = record;
                }
            }
        },
        watch: {
            perPage: function(perPage) {
                this.perPage = parseInt(perPage);

                if (this.currentPage > this.paginationData.last_page) {
                    this.paginateRecords(1);
                }
            }
        }
    }
</script>
<style>
    .inv-table-filter {
        float: left;
        display: inline-block;
        margin-bottom: 20px;
    }
    .inv-table-length {
        float: right;
        display: inline-block;
        margin-bottom: 20px;
    }
</style>